import { useCallback } from 'react';
import { toasterApi } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../../clients/testerClient';

type UseCoPilotFeedbackParams = {
  targetId: string | null;
  threadId: string | null;
};

type SubmitQuickRatingParams = {
  traceId: string;
  rating: 'positive' | 'negative';
};

type SubmitDetailedFeedbackParams = {
  traceId: string;
  feedback: string;
  screenshot?: { data: string; mimeType: string };
  rating?: 'positive' | 'negative' | null;
};

export function useCoPilotFeedback({
  targetId,
  threadId,
}: UseCoPilotFeedbackParams) {
  const handleQuickRating = useCallback(
    async ({ traceId, rating }: SubmitQuickRatingParams) => {
      if (!threadId || !targetId) {
        toasterApi.error({
          title: 'Cannot submit feedback',
          description: 'Missing required information',
        });
        return { success: false };
      }

      try {
        const result = await terraProxyApiClient.submitCoPilotFeedback({
          applicationId: targetId,
          threadId,
          traceId,
          rating,
        });

        if (!result) {
          throw new Error('Failed to submit feedback');
        }

        if (!result.success) {
          throw new Error(result.error || 'Failed to submit feedback');
        }

        toasterApi.success({
          title: 'Feedback submitted',
          description: 'Thank you for your feedback!',
        });

        return { success: true, rating };
      } catch (error) {
        toasterApi.error({
          title: 'Failed to submit rating',
          description:
            error instanceof Error ? error.message : 'Please try again later',
        });
        return { success: false, error };
      }
    },
    [threadId, targetId],
  );

  const handleFeedbackSubmit = useCallback(
    async ({
      traceId,
      feedback,
      screenshot,
      rating,
    }: SubmitDetailedFeedbackParams) => {
      if (!threadId || !targetId) {
        toasterApi.error({
          title: 'Cannot submit feedback',
          description: 'Missing required information',
        });
        return { success: false };
      }

      try {
        const result = await terraProxyApiClient.submitCoPilotFeedback({
          applicationId: targetId,
          threadId,
          traceId,
          feedback,
          screenshot,
          rating: rating ?? undefined,
        });

        if (!result) {
          throw new Error('Failed to submit feedback');
        }

        if (!result.success) {
          throw new Error(result.error || 'Failed to submit feedback');
        }

        toasterApi.success({
          title: 'Feedback submitted',
          description: 'Thank you for your feedback!',
        });

        return { success: true };
      } catch (error) {
        toasterApi.error({
          title: 'Failed to submit feedback',
          description: 'Please try again later',
        });
        throw error;
      }
    },
    [threadId, targetId],
  );

  return {
    handleQuickRating,
    handleFeedbackSubmit,
  };
}
